import React, { useEffect, useState } from 'react';

import { useGlobalLoader } from 'contexts/GlobalLoaderContext';
import { getAnsweredDetails } from 'apis/adminServices/dashboardService';
import { LoadDetail, LoadDetailsResponse } from './types';
import { Link } from 'react-router-dom';
import SearchBar from '../creditsManagement/searchbar';
import LoadFilter from './loadFilter';
import RowsPerPageSelect from "components/admin/RowsPerPageSelect";



const LoadAnswered: React.FC = () => {
    const [loadDetails, setLoadDetails] = useState<LoadDetail[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [fromDate, setFromDate] = useState<string | undefined>(undefined);
    const [toDate, setToDate] = useState<string | undefined>(undefined);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const { showLoader, hideLoader } = useGlobalLoader();

    // Reset pagination when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const fetchLoadDetails = async (page: number, size: number) => {
        try {
            setLoading(true);
            showLoader();
            const response: LoadDetailsResponse = await getAnsweredDetails(
                page, 
                size, 
                searchTerm || undefined,
                fromDate,
                toDate
            );
            // Handle 204 No Content or empty response
            if (!response || !response.items) {
                setLoadDetails([]);
                setTotalItems(0);
            } else {
                // Order is already applied server-side (chronological within the filtered
                // range); re-sorting here would only reorder the current page and fight it.
                setLoadDetails(response.items);
                setTotalItems(response.totalCount);
            }
        } catch (error) {
            console.error('Error fetching load details:', error);
            setLoadDetails([]);
            setTotalItems(0);
        } finally {
            hideLoader();
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLoadDetails(currentPage, pageSize);
    }, [currentPage, pageSize, searchTerm, fromDate, toDate]);

    const handleApplyFilters = (filters: {
        fromDate?: string;
        toDate?: string;
    }) => {
        setFromDate(filters.fromDate);
        setToDate(filters.toDate);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFromDate(undefined);
        setToDate(undefined);
        setCurrentPage(1);
    };

    // Calculate total pages
    const totalPages = Math.ceil(totalItems / pageSize);

    // Generate page numbers to display
    const generatePageNumbers = () => {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        // Adjust start page if we're near the end
        if (endPage === totalPages) {
            startPage = Math.max(1, totalPages - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    };

    // Format date to local string
    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('en-GB');
    };

    // Real event timestamps (e.g. answered date) are shown in the viewer's
    // local time — do NOT force UTC here (that's only for flight wall-clock
    // times, which are stored tagged with "Z"). Keeps the date + time on the
    // same timezone as formatDate above.
    const formatLocalTime = (dateString: string): string => {
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    // Format date and time

    // Flight date/time is stored as the flight's local wall-clock time tagged
    // with "Z", so format it in UTC (not the browser's timezone) to show it
    // as-is — matching the user side. Without timeZone: 'UTC' the browser adds
    // its own offset (e.g. +5:30), shifting the time and sometimes the date.
    const formatFlightDate = (dateString: string): string => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            timeZone: 'UTC',
        });
    };

    const formatFlightTime = (dateString: string): string => {
        // 24-hour clock in UTC to exactly match the user side (e.g. 21:25, not
        // 09:25 PM). Same instant either way, but the user side uses 24h so the
        // 12h notation read as a mismatch.
        return new Date(dateString).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'UTC',
        });
    };


    return (
        <div>
            <section className="flex flex-wrap items-center justify-between gap-2 px-6 pt-2">
                <p className="text-grease-100 text-lg font-semibold">Loads Answered</p>
                <nav className="flex flex-wrap items-center gap-2">
                    <span className="st-dashboard text-lightgray-200 text-xs" aria-hidden="true" />
                    <Link className="text-lightgray-200 text-sm" to="/admin/dashboard">Dashboard</Link>
                    <span className="st-arrow-right text-lightgray-200 text-xs" aria-hidden="true" />
                    <span className="text-royalblue-100 text-sm">Loads Answered</span>
                </nav>
            </section>
            <div className="px-6 pt-6">
                <section>
                    <div className="bg-white rounded-xl p-4 mt-2">
                        <div className="flex flex-wrap gap-4 items-center justify-between pb-6">
                            <p className="text-grease-100 text-sm font-semibold">Load Details</p>
                            <div className="flex flex-wrap items-center gap-3 relative">
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                                <div className="relative">
                                    <button
                                        id="dropdownDefaultButton-loadAnswered"
                                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                                        className="flex items-center justify-center gap-2 p-3 border bg-violet-200 rounded-lg"
                                    >
                                        <i className="st-filter text-white"></i>
                                        <p className="font-medium text-white text-sm">Filters</p>
                                    </button>
                                    <LoadFilter
                                        isOpen={isFilterOpen}
                                        onClose={() => setIsFilterOpen(false)}
                                        onApply={handleApplyFilters}
                                        onClear={handleClearFilters}
                                        dateRangeLabel="Answered Date Range"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="relative overflow-x-auto sm:rounded-lg">
                            <table className="w-full text-sm text-left text-gray-500">
                                <thead className="text-xs text-gray-700 p-4 bg-blue-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-4 rounded-l-lg">
                                            <p className="text-lightgray-200">S.No.</p>
                                        </th>
                                        <th scope="col" className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Answered By</p>
                                        </th>
                                        <th scope="col" className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Answered Date</p>
                                        </th>
                                        <th scope="col" className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Requested By</p>
                                        </th>
                                        <th scope="col" className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Flight Number</p>
                                        </th>
                                        <th scope="col" className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Flight Date & Time</p>
                                        </th>
                                        <th scope="col" className="px-6 py-4">
                                            <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">Tokens Received</p>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="rounded-lg">
                                    {loading ? (
                                        <tr>
                                            <td colSpan={6} className="text-center py-4">Loading...</td>
                                        </tr>
                                    ) : loadDetails.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="text-center py-4">No data found</td>
                                        </tr>
                                    ) : (
                                        loadDetails.map((detail, index) => (
                                            <tr key={index} className="odd:bg-white even:bg-blue-50 border">
                                                <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                                                    <p>{(currentPage - 1) * pageSize + index + 1}</p>
                                                </td>
                                                <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                                                    <p className="px-4">
                                                        {detail.answeredBy
                                                            ? detail.answeredBy
                                                                .toLowerCase()
                                                                .replace(/\b\w/g, (char) => char.toUpperCase())
                                                            : ''}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                                                    <p className="px-4">{formatDate(detail.answeredDate)}</p>
                                                    <p className="px-4 text-lightgray-200 text-xs">{formatLocalTime(detail.answeredDate)}</p>
                                                </td>
                                                <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                                                    <p className="px-4">
                                                        {detail.requestedBy
                                                            ? detail.requestedBy
                                                                .toLowerCase()
                                                                .replace(/\b\w/g, (char) => char.toUpperCase())
                                                            : ''}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                                                    <p className="px-4">{detail.flightNumber}</p>
                                                </td>
                                                <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                                                    <p className="px-4">{formatFlightDate(detail.flightDate)}</p>
                                                    <p className="px-4 text-lightgray-200 text-xs">{formatFlightTime(detail.flightDate)}</p>
                                                </td>
                                                <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                                                    <span className="px-4 inline-block min-w-[50px] text-center">{detail.creditReceived}</span>
                                                </td>

                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Section */}
                        <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                <p className="text-lightgray-200 text-sm">
                                    Total {totalItems} {totalItems === 1 ? "record" : "records"}
                                </p>
                                <div className="flex items-center gap-2">
                                    <p className="text-lightgray-200 text-sm">Rows per page</p>
                                    <RowsPerPageSelect
                                      value={pageSize}
                                      onChange={(size) => {
                                        setPageSize(size);
                                        setCurrentPage(1);
                                      }}
                                    />
                                </div>
                            </div>

                            <div className="w-full sm:w-auto">
                                <ul className="flex flex-wrap items-center justify-center my-6 gap-3 text-sm">
                                    {/* Previous Button */}
                                    <li>
                                        <button
                                            disabled={currentPage === 1}
                                            className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${currentPage === 1
                                                ? 'text-gray-300 border-gray-300'
                                                : 'text-primaryblue-50 border-primaryblue-50'
                                                }`}
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                        >
                                            <span className="sr-only">Previous</span>
                                            <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
                                        </button>
                                    </li>

                                    {/* Page Numbers */}
                                    {generatePageNumbers().map((pageNum) => (
                                        <li key={pageNum}>
                                            <button
                                                className={`flex items-center justify-center w-7 h-7 rounded-md ${currentPage === pageNum
                                                    ? 'text-white border border-primaryblue-50 bg-primaryblue-50'
                                                    : 'text-primaryblue-50 border border-primaryblue-50'
                                                    }`}
                                                onClick={() => setCurrentPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        </li>
                                    ))}

                                    {/* Next Button */}
                                    <li>
                                        <button
                                            disabled={currentPage === totalPages}
                                            className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${currentPage === totalPages
                                                ? 'text-gray-300 border-gray-300'
                                                : 'text-primaryblue-50 border-primaryblue-50'
                                                }`}
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                        >
                                            <span className="sr-only">Next</span>
                                            <i className="st-arrow-right text-xl font-semibold"></i>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default LoadAnswered;

